import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp, Search, Eye, Users, Sparkles, Save } from "lucide-react";
import { toast } from "sonner";

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState({ ga_measurement_id: "", search_console_code: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`${V1}/analytics/summary?days=30`).then((r) => setData(r.data));
    api.get(`${V1}/analytics/config`).then((r) => setCfg({ ga_measurement_id: r.data.ga_measurement_id || "", search_console_code: r.data.search_console_code || "" }));
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    try {
      await api.put(`${V1}/analytics/config`, {
        ga_measurement_id: cfg.ga_measurement_id || null,
        verification_code: cfg.search_console_code || null,
      });
      toast.success("Ayarlar kaydedildi");
    } catch { toast.error("Kaydedilemedi"); }
  };

  const autoPublish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${V1}/analytics/auto-publish-guides`, { limit: 5 });
      toast.success(`${data.count} rehber yayınlandı`);
    } catch { toast.error("Otomatik yayın başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div>
        <div className="overline text-accent">Analitik</div>
        <h1 className="font-display text-3xl font-bold">Site Analitiği & SEO</h1>
        <p className="text-muted-foreground mt-1">Son 30 gün — sayfa görüntülenme, aramalar ve popüler içerikler</p>
      </div>
      {data && (
        <div className="grid md:grid-cols-4 gap-4">
          <Metric icon={Eye} label="Sayfa Gösterimi" value={data.total_views} />
          <Metric icon={Users} label="Tekil Ziyaretçi" value={data.unique_sessions} />
          <Metric icon={Search} label="Arama Sayısı" value={data.total_searches} />
          <Metric icon={TrendingUp} label="Yayın Bloğu" value={data.top_blog_posts?.length || 0} />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="font-display font-bold text-lg mb-4">En Çok Görüntülenen Sayfalar</div>
          <div className="space-y-2 text-sm">
            {(data?.top_pages || []).length === 0 && <div className="text-muted-foreground">Henüz veri yok.</div>}
            {(data?.top_pages || []).map((p, i) => (
              <div key={i} className="flex justify-between border-b border-border py-2">
                <span className="font-mono text-xs truncate max-w-[70%]">{p.path}</span>
                <span className="font-mono font-bold">{p.views}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold text-lg">En Çok Aranan Sorgular</div>
            <Button size="sm" onClick={autoPublish} disabled={busy} data-testid="auto-publish-btn">
              <Sparkles className="h-3 w-3" /> {busy ? "Üretiliyor…" : "Otomatik Rehber Yayınla"}
            </Button>
          </div>
          <div className="space-y-2 text-sm">
            {(data?.top_searches || []).length === 0 && <div className="text-muted-foreground">Henüz veri yok.</div>}
            {(data?.top_searches || []).map((s, i) => (
              <div key={i} className="flex justify-between border-b border-border py-2">
                <span className="truncate">{s.query}</span>
                <span className="font-mono font-bold">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="font-display font-bold text-lg mb-4">En Popüler Blog Yazıları</div>
          <div className="space-y-2 text-sm">
            {(data?.top_blog_posts || []).map((p) => (
              <div key={p.slug} className="flex justify-between border-b border-border py-2">
                <span className="truncate max-w-[70%]">{p.title}</span>
                <span className="font-mono font-bold">{p.views}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="font-display font-bold text-lg mb-4">Google Entegrasyonu</div>
          <div className="space-y-3">
            <div>
              <Label>GA4 Ölçüm ID (G-XXXXXXXXXX)</Label>
              <Input value={cfg.ga_measurement_id} onChange={(e) => setCfg({ ...cfg, ga_measurement_id: e.target.value })} placeholder="G-XXXXXXXXXX" data-testid="ga-id" />
            </div>
            <div>
              <Label>Search Console Doğrulama Kodu</Label>
              <Input value={cfg.search_console_code} onChange={(e) => setCfg({ ...cfg, search_console_code: e.target.value })} placeholder="google-site-verification=..." data-testid="sc-code" />
              <div className="text-xs text-muted-foreground mt-1">Sayfa &lt;head&gt; içine meta tag olarak eklenir. Doğrulandığında Search Console'da kayıt olur.</div>
            </div>
            <Button onClick={saveCfg} data-testid="save-seo-cfg"><Save className="h-4 w-4" /> Kaydet</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Metric = ({ icon: Icon, label, value }) => (
  <div className="p-6 rounded-xl border border-border bg-card">
    <Icon className="h-6 w-6 text-accent mb-3" />
    <div className="text-3xl font-display font-bold">{value ?? "—"}</div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </div>
);
